import { FC, useState } from 'react';

import { isProjectStatusDeactivated } from 'redux/reducers/project/selectors';
import { Tab } from 'modules/common/components/Tab/Tab';
import { Alert } from 'modules/common/components/Alert/Alert';
import { TabGroup } from 'modules/common/components/Tab/TabGroup';
import { Bookmarks } from 'modules/edit/Bookmarks/Bookmarks';
import { FindAndApply } from 'modules/edit/FindAndApply/FindAndApply';
import { AudioSideBar } from 'modules/edit/AudioSideBar/AudioSideBar';
import { Tags } from 'modules/edit/Tags/Tags';
import SidebarContext from 'modules/common/context/SidebarContext';
import { Comments } from 'modules/edit/Comments/Comments';
import { useCanEdit } from 'modules/common/hooks/can-permission/useCanEdit';
import { useAppSelector } from 'modules/common/hooks/redux';
import { useCanComment } from 'modules/common/hooks/can-permission/useCanComment';
import { useCanSeeBookmark } from 'modules/common/hooks/can-permission/useCanSeeBookmark';
import { useIsProoferRole } from 'modules/common/hooks/useIsProoferRole';
import { useIsScorerRole } from 'modules/common/hooks/useIsScorerRole';
import Styles from './sidebar.module.scss';
import { Divider } from 'modules/common/components/Divider/Divider';
import { ScoringReport } from 'modules/edit/ScoringReport/ScoringReport';

enum Tabs {
  Edit = 0,
  FindAndApply,
  Bookmarks,
  Audio,
  Comments,
  CommentsWithAudio,
  ScoringReport,
}

type SidebarProps = {
  isSSML: boolean;
};

export const Sidebar: FC<SidebarProps> = ({ isSSML }) => {
  const canUserEdit = useCanEdit();
  const canUserComment = useCanComment();
  const canSeeBookmarks = useCanSeeBookmark();
  const isDeactivated = useAppSelector(isProjectStatusDeactivated);
  const isProofer = useIsProoferRole();
  const isScorer = useIsScorerRole();

  const getInitialTab = () => {
    if (canUserEdit) return Tabs.Edit;
    if (canSeeBookmarks) return Tabs.Bookmarks;
    if (isProofer && canUserComment) return Tabs.CommentsWithAudio;
    if (isScorer) return Tabs.ScoringReport;
    return Tabs.Audio;
  };

  const [activeTab, setActiveTab] = useState<Tabs>(getInitialTab());
  const [showAlert, setShowAlert] = useState(true);

  return (
    <SidebarContext.Provider value={{ showAlert: showAlert, setShowAlert }}>
      <div className={Styles['sidebar-header']}>
        <TabGroup underline className="p-l-3">
          {canUserEdit ? (
            <>
              <Tab
                active={activeTab === Tabs.Edit}
                onClick={() => setActiveTab(Tabs.Edit)}
              >
                Edit
              </Tab>
              <Tab
                active={activeTab === Tabs.FindAndApply}
                onClick={() => setActiveTab(Tabs.FindAndApply)}
              >
                Find & Apply
              </Tab>
            </>
          ) : (
            <></>
          )}
          {canSeeBookmarks ? (
            <Tab
              active={activeTab === Tabs.Bookmarks}
              onClick={() => setActiveTab(Tabs.Bookmarks)}
            >
              Bookmarks
            </Tab>
          ) : (
            <></>
          )}
          {isProofer && canUserComment ? (
            <Tab
              active={activeTab === Tabs.CommentsWithAudio}
              onClick={() => setActiveTab(Tabs.CommentsWithAudio)}
            >
              Comments & Audio
            </Tab>
          ) : (
            <>
              {canUserComment && (
                <Tab
                  active={activeTab === Tabs.Comments}
                  onClick={() => setActiveTab(Tabs.Comments)}
                >
                  Comments
                </Tab>
              )}
              <Tab
                active={activeTab === Tabs.Audio}
                onClick={() => setActiveTab(Tabs.Audio)}
              >
                Audio
              </Tab>
            </>
          )}
          {isScorer ? (
            <Tab
              active={activeTab === Tabs.ScoringReport}
              onClick={() => setActiveTab(Tabs.ScoringReport)}
            >
              Scoring Report
            </Tab>
          ): <></>}
        </TabGroup>
      </div>

      <div className={Styles['sidebar-content']}>
        {activeTab === Tabs.Bookmarks && <Bookmarks />}
        {activeTab === Tabs.FindAndApply && <FindAndApply />}
        {canUserEdit && activeTab === Tabs.Edit && <Tags isSSML={isSSML} />}
        {activeTab === Tabs.Audio && <AudioSideBar />}
        {canUserComment && activeTab === Tabs.Comments && <Comments />}
        {isProofer && canUserComment && activeTab === Tabs.CommentsWithAudio && (
          <div className={Styles['comments-with-audio']}>
            <Comments />
            <AudioSideBar />
          </div>
        )}
        {isScorer && activeTab === Tabs.ScoringReport && <ScoringReport />}
      </div>
      {isDeactivated && (
        <Alert
          icon="close_outline"
          className={Styles['deactivation-status-alert']}
          type="error"
          spacing={0}
        >
          The project was deactivated. Please stop the work on this project.
        </Alert>
      )}
    </SidebarContext.Provider>
  );
};
